/**
 * "When should you follow up next?" (R22), offered after a conversation is
 * logged by hand: from the outcome prompt, the Messages composer, "Log their
 * reply" and the lead-detail log dialog. Never after an import.
 *
 * A pick creates a reminder at 10:00 India time on the chosen day, on the
 * lead the log came from when that lead is open, else on the person's
 * most recently updated open lead (which is their only one when they have
 * one). With no open lead there is nothing to remind about, so the prompt
 * does not show.
 */

import { CalendarDays, Loader2 } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	useContact,
	useCreateFollowUpReminder,
} from "@/features/realtor/hooks/use-realtor-crm";
import type { PipelineStage } from "@/features/realtor/services/leads-api.service";
import { errorMessage } from "@/features/realtor/utils/error-message";
import { StudioButton } from "@/features/studio/theme";
import {
	addDays,
	indiaToday,
	PROMPT_CONTENT_CLASS,
	QUICK_PICKS,
	shortIndiaDate,
	tenAmIndia,
} from "./follow-up-format";

const CLOSED_STAGES: readonly PipelineStage[] = ["closed_won", "closed_lost"];

export interface ReminderLeadOption {
	id: string;
	pipelineStage: PipelineStage;
}

/** Who to follow up with, and where the log that prompted it came from. */
export interface NextStepTarget {
	/** Null for a lead with no contact; pass `leads` then. */
	contactId: string | null;
	contactName: string;
	/** The lead the conversation was logged against, if any. */
	fromLeadId: string | null;
	/**
	 * The person's leads, most recently updated first. Left out, they are
	 * read from the contact.
	 */
	leads?: ReminderLeadOption[];
}

/** The lead a follow-up reminder goes on, or null when none is open. */
export function chooseReminderLead(
	fromLeadId: string | null,
	leadsByRecency: ReminderLeadOption[],
): string | null {
	const open = leadsByRecency.filter(
		(lead) => !CLOSED_STAGES.includes(lead.pipelineStage),
	);
	if (fromLeadId && open.some((lead) => lead.id === fromLeadId)) {
		return fromLeadId;
	}
	return open[0]?.id ?? null;
}

export function NextStepPrompt({
	target,
	onClose,
}: {
	target: NextStepTarget | null;
	onClose: () => void;
}) {
	const contact = useContact(target && !target.leads ? target.contactId : null);
	const leads =
		target?.leads ??
		contact.data?.leads.map((lead) => ({
			id: lead.id,
			pipelineStage: lead.pipelineStage,
		}));
	const leadId =
		target && leads ? chooseReminderLead(target.fromLeadId, leads) : null;
	const resolvedWithoutLead =
		target !== null && (contact.isError || (leads && leadId === null));

	useEffect(() => {
		if (resolvedWithoutLead) onClose();
	}, [resolvedWithoutLead, onClose]);

	if (!target || !leadId) return null;
	return (
		<NextStepDialog
			key={`${target.contactId}:${leadId}`}
			target={target}
			leadId={leadId}
			onClose={onClose}
		/>
	);
}

function NextStepDialog({
	target,
	leadId,
	onClose,
}: {
	target: NextStepTarget;
	leadId: string;
	onClose: () => void;
}) {
	const { t, i18n } = useTranslation();
	const ids = useId();
	const createReminder = useCreateFollowUpReminder();
	const [picking, setPicking] = useState(false);
	const [picked, setPicked] = useState("");
	const [error, setError] = useState<string | null>(null);
	const busy = createReminder.isPending;
	const tomorrow = addDays(indiaToday(), 1);

	const remind = async (date: string) => {
		if (busy) return;
		setError(null);
		try {
			await createReminder.mutateAsync({
				contactId: target.contactId,
				leadId,
				title: t("realtor.followUps.nextStep.reminderTitle", {
					name: target.contactName,
				}),
				scheduledAt: tenAmIndia(date),
			});
			toast.success(
				t("realtor.followUps.nextStep.saved", {
					date: shortIndiaDate(date, i18n.language),
				}),
			);
			onClose();
		} catch (failure: unknown) {
			setError(errorMessage(failure, t("realtor.followUps.nextStep.failed")));
		}
	};

	return (
		<Dialog
			open
			onOpenChange={(next) => {
				if (!next && !busy) onClose();
			}}
		>
			<DialogContent className={PROMPT_CONTENT_CLASS}>
				<DialogHeader>
					<DialogTitle className="font-display text-xl italic font-medium text-dashboard-text">
						{t("realtor.followUps.nextStep.title")}
					</DialogTitle>
					<DialogDescription className="text-sm text-dashboard-muted">
						{t("realtor.followUps.nextStep.description", {
							name: target.contactName,
						})}
					</DialogDescription>
				</DialogHeader>
				<div className="flex flex-col gap-2">
					{QUICK_PICKS.map((pick) => (
						<StudioButton
							key={pick.id}
							variant="secondary"
							disabled={busy}
							onClick={() => void remind(addDays(indiaToday(), pick.days))}
						>
							{t(`realtor.followUps.picks.${pick.id}`)}
						</StudioButton>
					))}
					{picking ? (
						<form
							className="flex flex-col gap-2"
							onSubmit={(event) => {
								event.preventDefault();
								if (picked) void remind(picked);
							}}
						>
							<label
								htmlFor={`${ids}-date`}
								className="text-[11px] font-semibold uppercase tracking-wider text-dashboard-muted"
							>
								{t("realtor.followUps.picks.dateLabel")}
							</label>
							<input
								id={`${ids}-date`}
								type="date"
								min={tomorrow}
								value={picked}
								onChange={(event) => setPicked(event.target.value)}
								className="h-11 rounded-lg border border-dashboard-border bg-dashboard-bg px-3 text-sm text-dashboard-text"
							/>
							<StudioButton type="submit" disabled={!picked || busy}>
								{busy && <Loader2 className="h-4 w-4 animate-spin" />}
								{t("realtor.followUps.nextStep.setReminder")}
							</StudioButton>
						</form>
					) : (
						<StudioButton
							variant="secondary"
							icon={CalendarDays}
							disabled={busy}
							onClick={() => setPicking(true)}
						>
							{t("realtor.followUps.picks.pickDate")}
						</StudioButton>
					)}
					<StudioButton variant="ghost" disabled={busy} onClick={onClose}>
						{t("realtor.followUps.nextStep.skip")}
					</StudioButton>
				</div>
				{error && (
					<p role="alert" className="text-sm text-dashboard-pill-risk">
						{error}
					</p>
				)}
			</DialogContent>
		</Dialog>
	);
}
